import { useEffect, useState } from "react";
import { ApiError } from "@keydials/api-client";
import { Icon } from "@keydials/dial-workspace";
import { api, getAccessToken } from "../auth/AuthContext";

/** The server's limit (`InstanceService.RenameAsync`); mirrored here so the box stops where the save would fail. */
const MAX_NAME_LENGTH = 200;

/**
 * This dial's own name (KEY-671), shown to dial admins only.
 *
 * Reuses the rename web-admin already has (KEY-214) rather than a second path: the server decides who may
 * rename — an organisation instance-manager, or an Admin of the whole dial — and that is exactly who reaches
 * the Display settings this card sits in, so nobody is offered a button that would refuse them.
 */
export function DialNamePanel({
  instanceId,
  name,
  onChanged,
}: {
  instanceId: string;
  name: string;
  onChanged: () => void | Promise<void>;
}) {
  const [draft, setDraft] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Follow the saved name when it changes underneath (a reload, or a different dial in the same view).
  useEffect(() => {
    setDraft(name);
  }, [name]);

  const trimmed = draft.trim();
  const canSave = !busy && trimmed.length > 0 && trimmed !== name;

  async function save() {
    const token = getAccessToken();
    if (!token || !canSave) return;

    setBusy(true);
    setError(null);
    try {
      await api.renameInstance(token, instanceId, trimmed);
      await onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not rename this dial.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kd-card" style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
      <div className="kd-label-overline" style={{ display: "flex", alignItems: "center", gap: 7 }}>
        <Icon name="pencil" size={13} />
        Dial name
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13.5 }}
      >
        <label htmlFor="kd-dial-name">
          What this dial is called
          <span style={{ display: "block", color: "var(--text-muted)", fontSize: 12.5, marginTop: 2, lineHeight: 1.45 }}>
            Everyone on the dial sees the new name the next time they open it.
          </span>
        </label>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            id="kd-dial-name"
            type="text"
            value={draft}
            maxLength={MAX_NAME_LENGTH}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            style={{ flex: 1, minWidth: 0, font: "inherit", fontSize: 13, padding: "5px 8px", borderRadius: 6, border: "var(--border-w) solid var(--border)", background: "var(--surface)", color: "var(--text)" }}
          />
          <button type="submit" className="kd-btn-primary" disabled={!canSave}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>

      {error && <div role="alert" style={{ color: "var(--st-overdue)", fontSize: 12.5 }}>{error}</div>}
    </div>
  );
}
